/**
 * Memberships - spec 06's admin routes, reached from spec 12's nav.
 *
 * Memberships belong to a member, so this screen is a member picker followed by
 * that member's records. The picker is a search rather than a full list because
 * an admin arrives here knowing WHO they are fixing - "the member at the desk" -
 * not browsing for records.
 *
 * Two database rules surface as copy rather than as a crash, and both come back
 * from the server as sentences:
 *
 * - **`memberships_no_overlap`** (a GiST exclusion constraint) refuses dates that
 *   overlap a membership the member already holds. A renewal must start the day
 *   AFTER the previous record expires. That is a 409 `membership_overlap`.
 * - **`membership_extensions_one_per_record`** allows one extension per
 *   membership (FR-3.5.7), after which the record is locked and a new membership
 *   is the answer. The Extend button is disabled rather than hidden, so the reason
 *   stays visible.
 *
 * Money is shown here and only here: `amount_paid`, `payment_date`,
 * `payment_method` and `notes` never appear in a member-facing payload.
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardHeader,
  Definition,
  DefinitionList,
  ErrorNotice,
  EmptyState,
  Field,
  Input,
  LoadingRows,
  Modal,
  Page,
  Select,
  SuccessNotice,
  Textarea,
} from "../components/primitives";
import { fetchMembers, type MemberListItem } from "../lib/api/accounts";
import {
  createMembership,
  extendMembership,
  fetchMemberships,
  updateMembership,
  type Membership,
  type MembershipCreate,
  type MembershipList,
} from "../lib/api/memberships";
import type { Page as PageType, PaymentMethod } from "../lib/api/types";
import { useMutation, useQuery } from "../lib/hooks";
import { formatDate, formatMoney, humanizeStatus } from "../lib/format";

const SEARCH_PAGE = 10;
const PAYMENT_METHODS: PaymentMethod[] = ["cash", "esewa", "khalti", "bank_transfer", "card", "other"];

export function MembershipsScreen() {
  const [params, setParams] = useSearchParams();
  const memberId = params.get("member_id") ?? "";
  const [term, setTerm] = useState(params.get("q") ?? "");

  function selectMember(member: MemberListItem) {
    const next = new URLSearchParams(params);
    next.set("member_id", member.id);
    next.set("q", member.full_name);
    setParams(next, { replace: true });
  }

  /** Only search when no member is chosen: once one is, the search box is a
   *  "change member" control and firing a request as the admin types into it
   *  would be noise. */
  const search = useQuery<PageType<MemberListItem>>(
    () => fetchMembers({ q: term.trim() || undefined, limit: SEARCH_PAGE, offset: 0 }),
    [],
  );

  return (
    <Page>
      <Card>
        <CardHeader
          title="Find a member"
          hint="Search by name, email or mobile number, then open their memberships."
        />
        <div className="filters">
          <div style={{ width: 300 }}>
            <Field label="Member">
              {(id) => (
                <Input
                  id={id}
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="Shreya Karki, 9800000001…"
                  maxLength={254}
                />
              )}
            </Field>
          </div>
          <Button variant="primary" onClick={search.reload} disabled={search.refreshing}>
            {search.refreshing ? "Searching…" : "Search"}
          </Button>
          {memberId ? (
            <Button
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("member_id");
                setParams(next, { replace: true });
              }}
            >
              Change member
            </Button>
          ) : null}
        </div>

        {search.error ? (
          <div style={{ marginTop: 16 }}>
            <ErrorNotice error={search.error} onRetry={search.reload} />
          </div>
        ) : null}

        {search.data && search.data.items.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {search.data.items.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <div className="table__strong">{member.full_name}</div>
                        <div className="table__sub">
                          {member.mobile_number}
                          {member.email ? ` · ${member.email}` : ""}
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Button
                          size="sm"
                          variant={member.id === memberId ? "accent" : "ghost"}
                          onClick={() => selectMember(member)}
                        >
                          {member.id === memberId ? "Selected" : "Select"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {search.data.total > search.data.items.length ? (
              <p className="text-small text-meta" style={{ padding: "8px 16px 0" }}>
                Showing {search.data.items.length} of {search.data.total}. Narrow the search to
                see the rest.
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>

      {memberId ? (
        <MemberMemberships memberId={memberId} />
      ) : (
        <Card>
          <EmptyState
            title="No member selected."
            body="Search for a member above to see and manage their memberships."
          />
        </Card>
      )}
    </Page>
  );
}

function MemberMemberships({ memberId }: { memberId: string }) {
  const [saved, setSaved] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { kind: "create" } | { kind: "edit"; membership: Membership } | { kind: "extend"; membership: Membership } | null
  >(null);

  const query = useQuery<MembershipList>(() => fetchMemberships(memberId), [memberId]);
  const { data, error, loading, reload } = query;

  if (error) return <ErrorNotice error={error} onRetry={reload} />;
  if (loading && !data) {
    return (
      <Card flush>
        <LoadingRows rows={3} columns={5} />
      </Card>
    );
  }
  if (!data) return null;

  return (
    <>
      {saved ? <SuccessNotice message={saved} onDismiss={() => setSaved(null)} /> : null}

      <Card>
        <CardHeader
          title="App access"
          hint="Access as the member's own app sees it, from the same data."
        />
        <DefinitionList>
          <Definition term="Access">
            {data.access.has_app_access ? (
              <Badge tone="live">
                <span className="dot" />
                Active
              </Badge>
            ) : (
              <Badge tone="danger">No access</Badge>
            )}
          </Definition>
          <Definition term="Days remaining">
            {/* The view goes negative after expiry; the member's own screen clamps
                it at 0, so a negative number here would disagree with them. */}
            {data.access.days_remaining !== null && data.access.days_remaining !== undefined
              ? Math.max(0, data.access.days_remaining)
              : "-"}
          </Definition>
          <Definition term="In grace period">
            {data.access.in_grace_period ? "Yes" : "No"}
          </Definition>
          <Definition term="Access until">{formatDate(data.access.access_until)}</Definition>
        </DefinitionList>
      </Card>

      <Card flush>
        <div style={{ padding: "24px 24px 16px" }}>
          <CardHeader
            title="Membership records"
            hint={
              data.items.length === 0
                ? "This member has never had a membership recorded."
                : `${data.items.length} record${data.items.length === 1 ? "" : "s"}. Dates cannot overlap, so a renewal starts the day after the previous one ends.`
            }
            actions={
              <Button size="sm" variant="accent" onClick={() => setDialog({ kind: "create" })}>
                New membership
              </Button>
            }
          />
        </div>

        {data.items.length === 0 ? (
          <EmptyState
            title="No membership records."
            body="Add one so the member can use the app - access requires an active membership."
            action={
              <Button size="sm" variant="accent" onClick={() => setDialog({ kind: "create" })}>
                New membership
              </Button>
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Plan</th>
                  <th scope="col">Period</th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Paid
                  </th>
                  <th scope="col">Payment</th>
                  <th scope="col">Extension</th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((membership) => (
                  <tr key={membership.id}>
                    <td>
                      <div className="table__strong">{membership.plan_name}</div>
                      {membership.plan_features.length > 0 ? (
                        <ul className="bullets" style={{ margin: "4px 0 0", paddingLeft: "1em" }}>
                          {membership.plan_features.slice(0, 3).map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                          {membership.plan_features.length > 3 ? (
                            <li>+{membership.plan_features.length - 3} more</li>
                          ) : null}
                        </ul>
                      ) : null}
                    </td>
                    <td>
                      <div className="table__strong">
                        {formatDate(membership.start_date)} → {formatDate(membership.expiry_date)}
                      </div>
                      <div className="table__sub">
                        {membership.days_remaining >= 0
                          ? `${membership.days_remaining} days left`
                          : `${Math.abs(membership.days_remaining)} days ago`}
                        {membership.original_expiry_date !== membership.expiry_date
                          ? ` · originally ${formatDate(membership.original_expiry_date)}`
                          : ""}
                      </div>
                    </td>
                    <td className="table__num">
                      {membership.amount_paid ? formatMoney(membership.amount_paid, membership.currency) : "-"}
                    </td>
                    <td>
                      <div>
                        {membership.payment_method
                          ? humanizeStatus(membership.payment_method)
                          : "Not recorded"}
                      </div>
                      <div className="table__sub">
                        {membership.payment_date ? formatDate(membership.payment_date) : "-"}
                      </div>
                    </td>
                    <td>
                      {membership.extension ? (
                        <>
                          <div className="table__strong">
                            {formatDate(membership.extension.previous_expiry)} →{" "}
                            {formatDate(membership.extension.new_expiry)}
                          </div>
                          <div className="table__sub">{membership.extension.reason}</div>
                        </>
                      ) : (
                        <span className="text-meta">Not extended</span>
                      )}
                    </td>
                    <td>
                      <div className="table__actions">
                        <Button
                          size="sm"
                          onClick={() => setDialog({ kind: "edit", membership })}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="accent"
                          // FR-3.5.7: one extension per record, then it is locked.
                          disabled={membership.is_locked}
                          title={
                            membership.is_locked
                              ? "This membership has already been extended once."
                              : undefined
                          }
                          onClick={() => setDialog({ kind: "extend", membership })}
                        >
                          Extend
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {dialog?.kind === "create" ? (
        <MembershipFormDialog
          memberId={memberId}
          onClose={() => setDialog(null)}
          onDone={(message) => {
            setDialog(null);
            setSaved(message);
            reload();
          }}
        />
      ) : null}

      {dialog?.kind === "edit" ? (
        <MembershipEditDialog
          memberId={memberId}
          membership={dialog.membership}
          onClose={() => setDialog(null)}
          onDone={(message) => {
            setDialog(null);
            setSaved(message);
            reload();
          }}
        />
      ) : null}

      {dialog?.kind === "extend" ? (
        <ExtendDialog
          memberId={memberId}
          membership={dialog.membership}
          onClose={() => setDialog(null)}
          onDone={(message) => {
            setDialog(null);
            setSaved(message);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function MembershipFormDialog({
  memberId,
  onClose,
  onDone,
}: {
  memberId: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [planName, setPlanName] = useState("");
  const [features, setFeatures] = useState("");
  const [startDate, setStartDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [method, setMethod] = useState<PaymentMethod | "">("");
  const [notes, setNotes] = useState("");

  const mutation = useMutation(
    () => {
      const payload: MembershipCreate = {
        plan_name: planName.trim(),
        start_date: startDate,
        expiry_date: expiryDate,
      };
      // Only send what was filled in: the API distinguishes "absent" from "null"
      // for several of these, and an empty string would be a value.
      const parsedFeatures = features
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      if (parsedFeatures.length > 0) payload.plan_features = parsedFeatures;
      if (amount.trim()) payload.amount_paid = amount.trim();
      if (paymentDate) payload.payment_date = paymentDate;
      if (method) payload.payment_method = method;
      if (notes.trim()) payload.notes = notes.trim();
      return createMembership(memberId, payload);
    },
    { onSuccess: (result) => onDone(result?.message ?? "Membership created.") },
  );

  const blocked = !planName.trim() || !startDate || !expiryDate;

  return (
    <Modal
      title="New membership"
      description="The member gets app access as soon as this record covers today. Dates must not overlap a record they already hold."
      onClose={onClose}
      wide
    >
      <div className="stack" style={{ gap: 16 }}>
        <div className="form-grid">
          <Field label="Plan name (required)">
            {(id) => (
              <Input
                id={id}
                value={planName}
                onChange={(event) => setPlanName(event.target.value)}
                placeholder="6 month plan"
                maxLength={120}
              />
            )}
          </Field>

          <Field label="Start date (required)">
            {(id) => (
              <Input id={id} type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            )}
          </Field>

          <Field label="Expiry date (required)">
            {(id) => (
              <Input id={id} type="date" value={expiryDate} onChange={(event) => setExpiryDate(event.target.value)} />
            )}
          </Field>

          <Field label="Amount paid" hint="Admin-only. Never shown to the member.">
            {(id) => (
              <Input
                id={id}
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="12000"
              />
            )}
          </Field>

          <Field label="Payment date">
            {(id) => (
              <Input id={id} type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} />
            )}
          </Field>

          <Field label="Payment method">
            {(id) => (
              <Select
                id={id}
                value={method}
                onChange={(event) => setMethod(event.target.value as PaymentMethod | "")}
              >
                <option value="">Not recorded</option>
                {PAYMENT_METHODS.map((value) => (
                  <option key={value} value={value}>
                    {humanizeStatus(value)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <div className="form-grid__wide">
            <Field label="Plan features" hint="One per line. Snapshotted onto the record.">
              {(id) => (
                <Textarea
                  id={id}
                  value={features}
                  onChange={(event) => setFeatures(event.target.value)}
                  placeholder={"Full gym access\nOne guest pass per month"}
                />
              )}
            </Field>
          </div>

          <div className="form-grid__wide">
            <Field label="Notes" hint="Admin-only.">
              {(id) => (
                <Textarea id={id} value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={2000} />
              )}
            </Field>
          </div>
        </div>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button
            variant="accent"
            disabled={blocked || mutation.pending}
            onClick={() => void mutation.run()}
          >
            {mutation.pending ? "Saving…" : "Create membership"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function MembershipEditDialog({
  memberId,
  membership,
  onClose,
  onDone,
}: {
  memberId: string;
  membership: Membership;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [planName, setPlanName] = useState(membership.plan_name);
  const [features, setFeatures] = useState(membership.plan_features.join("\n"));
  const [amount, setAmount] = useState(membership.amount_paid ?? "");
  const [paymentDate, setPaymentDate] = useState(membership.payment_date ?? "");
  const [method, setMethod] = useState<PaymentMethod | "">(membership.payment_method ?? "");
  const [notes, setNotes] = useState(membership.notes ?? "");

  const mutation = useMutation(
    () =>
      updateMembership(memberId, membership.id, {
        plan_name: planName.trim(),
        plan_features: features
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        amount_paid: amount.trim(),
        payment_date: paymentDate || undefined,
        payment_method: method || undefined,
        notes: notes.trim(),
      }),
    { onSuccess: (result) => onDone(result?.message ?? "Membership updated.") },
  );

  return (
    <Modal
      title="Edit membership"
      description={`${membership.plan_name} · ${formatDate(membership.start_date)} → ${formatDate(membership.expiry_date)}. Dates are not editable; extend the record instead.`}
      onClose={onClose}
      wide
    >
      <div className="stack" style={{ gap: 16 }}>
        <div className="form-grid">
          <Field label="Plan name">
            {(id) => (
              <Input id={id} value={planName} onChange={(event) => setPlanName(event.target.value)} maxLength={120} />
            )}
          </Field>
          <Field label="Amount paid" hint="Admin-only.">
            {(id) => (
              <Input id={id} inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
            )}
          </Field>
          <Field label="Payment date">
            {(id) => (
              <Input id={id} type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} />
            )}
          </Field>
          <Field label="Payment method">
            {(id) => (
              <Select id={id} value={method} onChange={(event) => setMethod(event.target.value as PaymentMethod | "")}>
                <option value="">Not recorded</option>
                {PAYMENT_METHODS.map((value) => (
                  <option key={value} value={value}>
                    {humanizeStatus(value)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="form-grid__wide">
            <Field label="Plan features" hint="One per line.">
              {(id) => (
                <Textarea id={id} value={features} onChange={(event) => setFeatures(event.target.value)} />
              )}
            </Field>
          </div>
          <div className="form-grid__wide">
            <Field label="Notes" hint="Admin-only.">
              {(id) => (
                <Textarea id={id} value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={2000} />
              )}
            </Field>
          </div>
        </div>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button variant="primary" disabled={mutation.pending} onClick={() => void mutation.run()}>
            {mutation.pending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ExtendDialog({
  memberId,
  membership,
  onClose,
  onDone,
}: {
  memberId: string;
  membership: Membership;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [newExpiry, setNewExpiry] = useState("");
  const [reason, setReason] = useState("");

  const mutation = useMutation(
    () => extendMembership(memberId, membership.id, newExpiry, reason.trim()),
    { onSuccess: (result) => onDone(result?.message ?? "Membership extended.") },
  );

  return (
    <Modal
      title="Extend this membership"
      description={
        <>
          Current expiry is <strong>{formatDate(membership.expiry_date)}</strong>. The new date must
          be later than that, and this can only be done once - after this, the record is locked and
          a new membership is the way to extend cover again.
        </>
      }
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 16 }}>
        <Field label="New expiry date (required)">
          {(id) => (
            <Input
              id={id}
              type="date"
              value={newExpiry}
              // The browser's own guard, so the most common mistake never leaves
              // the dialog; the server refuses it anyway with a 422.
              min={nextDay(membership.expiry_date)}
              onChange={(event) => setNewExpiry(event.target.value)}
            />
          )}
        </Field>

        <Field label="Reason (required)" hint="Recorded on the extension and in the audit log.">
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={500}
              placeholder="Gym was closed for a week in October."
            />
          )}
        </Field>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button
            variant="accent"
            disabled={!newExpiry || reason.trim() === "" || mutation.pending}
            onClick={() => void mutation.run()}
          >
            {mutation.pending ? "Extending…" : "Extend"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function nextDay(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed.toISOString().slice(0, 10);
}
